import React, { useCallback, useEffect } from "react";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { PICGroupWithCategories } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationPublicId, UUIDString } from "../../../../common/service/server/Types";
import { CollectionCheckboxGroup } from "./CollectionCheckboxGroup";

type CollectionCheckboxesProps = {
  categoryGroups: PICGroupWithCategories[];
  orgId: OrganizationPublicId;
  group: CollectionGroupDetailsDto;
  collected: UUIDString[];
  pending: UUIDString[];
  autoselected?: string[];
  disabledCategoryIds?: UUIDString[];
  containerClassName?: string;
  onChange?: (collectedIds: string[]) => void;
  disabledTooltipFn?: (checked: boolean) => string;
};

export const CollectionCheckboxes: React.FC<CollectionCheckboxesProps> = ({
  categoryGroups,
  orgId,
  group,
  collected,
  pending,
  autoselected,
  disabledCategoryIds = [],
  containerClassName = "my-lg",
  disabledTooltipFn,
  onChange,
}) => {
  const {
    data: dataMap,
    mutators: { patch },
    refresh,
  } = useDataMap(orgId);

  const handleChecked = useCallback(
    (id: UUIDString, isCollected: boolean) => {
      patch.mutate({
        operations: [
          {
            action: isCollected ? "ADD" : "REMOVE",
            target: "COLLECTION",
            picId: id,
            groupId: group.id,
          },
        ],
      });
    },
    [group, patch],
  );

  useEffect(() => {
    if (onChange) {
      onChange(collected);
    }
    // onChange is not usually memoized and causes a runaway
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collected]);

  return (
    <>
      <div className={containerClassName}>
        {categoryGroups.map((picGroup) => {
          return (
            <CollectionCheckboxGroup
              key={picGroup.id}
              groupKey={picGroup.key}
              orgId={orgId}
              refreshDataMap={refresh}
              {...picGroup}
              dataMap={dataMap}
              collectedIds={collected}
              pendingIds={pending}
              onChecked={handleChecked}
              disabledCategoryIds={disabledCategoryIds}
              disabledTooltipFn={disabledTooltipFn}
              autoselected={autoselected}
              collectionGroupId={group.id}
            />
          );
        })}
      </div>
    </>
  );
};
